import React, { useEffect, useState, useRef, useCallback } from "react";
import apiCache from "../../services/apiCache";
import "../../assets/css/modern-restaurant.css";
import CuponSlide from "../Slider/CuponSlide";
import { useDispatch, useSelector } from "react-redux";
import toastify from "../../helper/toastify";
import {
  loadingStart,
  loadingStop,
} from "../../redux/features/loading/loadingSlice";
import { fetchPetpoojaMenu } from "../../redux/features/petpooja/petpoojaSlice";
import { transformPetpoojaMenu, transformPetpoojaCategories } from "../../utils/petpoojaTransformers";
import { axiosInstance, baseUrl } from "../../helper/axiosConfig";
import Recommended from "./RecommendedItem";
import { useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import TrackingBar from "../Order/TrackingBar";
import MobileBottomBar from "../Layout/MobileBottomBar";
import DownloadAppBanner from "../DownloadApp/DownloadAppBanner";
import norestimage from "../../assets/images/norestimage.png";
import { filterExpiredCoupons } from "../../utils/couponUtils";

function RestaurentList() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const location = useLocation();
  const [restaurantInformation, setRestaurantInformation] = useState({});
  const [restaurantMenu, setRestaurantMenu] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPetpoojaIntegrated, setIsPetpoojaIntegrated] = useState(false);
  const [petpoojaID, setPetpoojaID] = useState(null);
  const [filter, setFilter] = useState({
    user_id: id,
    search_text: "",
    rating: "",
    type: "",
    short_by: "",
    menu_category_id: "",
  });
  const dealsSliderRef = useRef(null);
  const fetchingRef = useRef(false);
  const lastFilterRef = useRef(null);

  // Get Petpooja data from Redux
  const { petpoojaMenus, loading: petpoojaLoading } = useSelector(state => state.petpooja);

  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      user_id: id,
      search_text: "",
      rating: "",
      type: "",
      short_by: "",
      menu_category_id: "",
    }));
  }, [id]);

  const fetchRestauentData = useCallback(async () => {
    // Prevent duplicate calls
    if (fetchingRef.current) {
      return;
    }

    // Create filter key to check if it's the same request
    const filterKey = JSON.stringify({
      user_id: filter.user_id,
      rating: filter.rating == "all" ? "" : filter.rating,
      short_by: filter.short_by == "all" ? "" : filter.short_by,
      type: filter.type == "all" ? "" : filter.type,
      menu_category_id: filter.menu_category_id || "",
    });

    // Skip if same filter was just fetched
    if (lastFilterRef.current === filterKey) {
      return;
    }

    fetchingRef.current = true;
    lastFilterRef.current = filterKey;
    setIsLoading(true);
    dispatch(loadingStart());

    try {
      // Check cache first
      const cacheKey = apiCache.getCacheKey("/restaurant-detail-for-web", "POST", {}, {
        user_id: filter.user_id,
        rating: filter.rating == "all" ? "" : filter.rating,
        short_by: filter.short_by == "all" ? "" : filter.short_by,
        type: filter.type == "all" ? "" : filter.type,
        menu_category_id: filter.menu_category_id || "",
      });
      
      const cachedData = apiCache.get(cacheKey, 120000); // 2 minutes cache
      
      if (cachedData) {
        const restaurantData = cachedData.restaurant_information;
        const filteredRestaurantData = {
          ...restaurantData,
          offer: filterExpiredCoupons(restaurantData?.offer || [])
        };
        setRestaurantInformation(filteredRestaurantData);
        
        const isIntegrated = restaurantData.isPetPoojaIntegrated;
        const petpoojaRestID = restaurantData.petPoojaID;
        
        setIsPetpoojaIntegrated(isIntegrated);
        setPetpoojaID(petpoojaRestID);

        if (isIntegrated && petpoojaRestID) {
          try {
            await dispatch(fetchPetpoojaMenu(petpoojaRestID));
          } catch (error) {
            console.error('Error fetching Petpooja menu:', error);
            setRestaurantMenu(cachedData.menu);
          }
        } else {
          setRestaurantMenu(cachedData.menu);
        }
        
        setIsLoading(false);
        dispatch(loadingStop());
        fetchingRef.current = false;
        return;
      }

      // Check if request is already pending
      if (apiCache.isPending(cacheKey)) {
        const pendingResponse = await apiCache.getPending(cacheKey);
        if (pendingResponse?.data?.status_code === 200) {
          const restaurantData = pendingResponse.data.data.restaurant_information;
          const filteredRestaurantData = {
            ...restaurantData,
            offer: filterExpiredCoupons(restaurantData?.offer || [])
          };
          setRestaurantInformation(filteredRestaurantData);
          
          const isIntegrated = restaurantData.isPetPoojaIntegrated;
          const petpoojaRestID = restaurantData.petPoojaID;
          
          setIsPetpoojaIntegrated(isIntegrated);
          setPetpoojaID(petpoojaRestID);

          if (isIntegrated && petpoojaRestID) {
            try {
              await dispatch(fetchPetpoojaMenu(petpoojaRestID));
            } catch (error) {
              console.error('Error fetching Petpooja menu:', error);
              setRestaurantMenu(pendingResponse.data.data.menu);
            }
          } else {
            setRestaurantMenu(pendingResponse.data.data.menu);
          }
        }
        setIsLoading(false);
        dispatch(loadingStop());
        fetchingRef.current = false;
        return;
      }

      const requestPromise = axiosInstance.post(
        `${baseUrl}/restaurant-detail-for-web`,
        {
          ...filter,
          rating: filter.rating == "all" ? "" : filter.rating,
          short_by: filter.short_by == "all" ? "" : filter.short_by,
          type: filter.type == "all" ? "" : filter.type,
          menu_category_id: filter.menu_category_id || "",
        },
        {
          headers: {
            Authorization: `Bearer ${
              localStorage.getItem("token") ||
              "209|dPB3cNB9hkGKGZvaFmV6yAKywcNZdB6yS5gMphb7baa4a560"
            }`,
          },
        }
      );
      
      apiCache.setPending(cacheKey, requestPromise);
      const res = await requestPromise;

      if (res.data.status_code === 200) {
        // Cache the response
        apiCache.set(cacheKey, res.data.data);
        
        const restaurantData = res.data.data.restaurant_information;
        // Filter expired coupons from restaurant offers
        const filteredRestaurantData = {
          ...restaurantData,
          offer: filterExpiredCoupons(restaurantData?.offer || [])
        };
        setRestaurantInformation(filteredRestaurantData);
        
        // DEBUG: Log restaurant data
        console.log('🔍 [RESTAURANT DEBUG] Restaurant data from /restaurant-detail-for-web API:', {
          user_id: restaurantData.user_id,
          restaurant_name: restaurantData.restaurant_name,
          isPetPoojaIntegrated: restaurantData.isPetPoojaIntegrated,
          petPoojaID: restaurantData.petPoojaID
        });
        
        // Check if restaurant is integrated with Petpooja
        const isIntegrated = restaurantData.isPetPoojaIntegrated;
        const petpoojaRestID = restaurantData.petPoojaID;
        
        setIsPetpoojaIntegrated(isIntegrated);
        setPetpoojaID(petpoojaRestID);

        if (isIntegrated && petpoojaRestID) {
          // Fetch menu from Petpooja
          try {
            await dispatch(fetchPetpoojaMenu(petpoojaRestID));
          } catch (error) {
            console.error('Error fetching Petpooja menu:', error);
            // Fallback to regular menu if Petpooja fails
            setRestaurantMenu(res.data.data.menu);
          }
        } else {
          // Use regular menu
          setRestaurantMenu(res.data.data.menu);
        }
      } else {
        toastify.error(res.data.message);
      }
    } catch (error) {
      console.error("Error fetching restaurant data:", error);
    } finally {
      setIsLoading(false);
      dispatch(loadingStop());
      fetchingRef.current = false;
    }
  }, [filter, dispatch]);

  // Get the appropriate menu data based on integration status
  const getMenuData = () => {
    if (isPetpoojaIntegrated && petpoojaID && petpoojaMenus[petpoojaID]) {
      // Transform Petpooja menu data to match your existing structure
      // Use user_id for transformation (as it was working)
      const restaurantIdFromDetail = restaurantInformation?.user_id;
      
      // DEBUG: Log transformation details
      console.log('🔍 [MENU DEBUG] Transforming Petpooja menu with:', {
        petpoojaID,
        restaurantIdFromDetail,
        isPetpoojaIntegrated
      });
      
      const transformedMenu = transformPetpoojaMenu(petpoojaMenus[petpoojaID], restaurantIdFromDetail);
      
      // CRITICAL FIX: After transformation, map the restaurant 'id' to 'restaurant_id_new'
      // This ensures the cart uses the correct ID for API calls
      const correctRestaurantId = restaurantInformation?.id;
      if (correctRestaurantId) {
        transformedMenu.forEach(item => {
          item.restaurant_id_new = correctRestaurantId;
          item.restaurant_id = correctRestaurantId;
        });
      }
      
      // DEBUG: Log transformed menu items (first few)
      if (transformedMenu.length > 0) {
        console.log('🔍 [MENU DEBUG] First transformed menu item:', {
          id: transformedMenu[0].id,
          item_name: transformedMenu[0].item_name,
          restaurant_id_new: transformedMenu[0].restaurant_id_new,
          restaurant_id: transformedMenu[0].restaurant_id,
          correctRestaurantId: correctRestaurantId
        });
      }
      
      return transformedMenu;
    }
    return Array.isArray(restaurantMenu) ? restaurantMenu : [];
  };

  // Get the appropriate categories for menu filtering
  const getMenuCategories = () => {
    if (isPetpoojaIntegrated && petpoojaID && petpoojaMenus[petpoojaID]) {
      return transformPetpoojaCategories(petpoojaMenus[petpoojaID]);
    }
    return []; // You might need to fetch categories separately for regular menu
  };

  // Consolidated: Single useEffect to handle all filter changes
  useEffect(() => {
    if (id) {
      fetchRestauentData();
    }
  }, [id, location.key, filter.rating, filter.type, filter.short_by, filter.menu_category_id, fetchRestauentData]);

  return (
    <>
      {/* Modern Restaurant Header Section */}
      <section className="modern-restaurant-header">
        <div className="container">
          <div className="restaurant-hero-card">
            {/* Restaurant Image */}
            <div className="restaurant-hero-image">
              <img 
                src={restaurantInformation?.restaurant_image_single || norestimage} 
                alt={restaurantInformation?.restaurant_name || "Restaurant"}
                className="hero-image"
              />
              <div className="hero-overlay"></div>
            </div>
            
            {/* Restaurant Information */}
            <div className="restaurant-info-container">
              <div className="restaurant-info-card">
                <div className="restaurant-header">
                  <h1 className="restaurant-name">
                    {restaurantInformation?.restaurant_name || "Restaurant"}
                  </h1>
                  
                  {/* Petpooja Integration Badge */}
                  {/* {isPetpoojaIntegrated && (
                    <div className="petpooja-badge">
                      <span className="badge bg-success">Petpooja Integrated</span>
                    </div>
                  )} */}
                  
                  {/* Rating and Reviews */}
                  <div className="rating-section">
                    <div className="rating-badge">
                      <i className="fas fa-star rating-star"></i>
                      <span className="rating-text">
                        {restaurantInformation?.rating?.toFixed(1) || "0.0"}
                      </span>
                    </div>
                    <span className="reviews-count">
                      {restaurantInformation?.updated_rating || 0} reviews
                    </span>
                  </div>
                </div>

                {/* Restaurant Details */}
                <div className="restaurant-details">
                  <div className="detail-item">
                    <i className="fas fa-map-marker-alt detail-icon location-icon"></i>
                    <span className="detail-text">
                      {restaurantInformation?.full_address || "Address not available"}
                    </span>
                  </div>
                  
                  <div className="detail-item">
                    <i className="far fa-clock detail-icon time-icon"></i>
                    <span className="detail-text">
                      {restaurantInformation?.delivery_time || "Delivery time not available"}
                    </span>
                    {restaurantInformation?.distance && (
                      <>
                        <span className="separator">•</span>
                        <span className="detail-text">
                          {restaurantInformation.distance}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="quick-actions">
                  <button className="action-btn primary-btn">
                    <i className="fas fa-phone"></i>
                    <span>Call Now</span>
                  </button>
                  <button className="action-btn secondary-btn">
                    <i className="fas fa-share-alt"></i>
                    <span>Share</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deals Section - Only show if there are valid (non-expired) offers */}
      {(() => {
        const validOffers = filterExpiredCoupons(restaurantInformation?.offer || []);
        return validOffers.length > 0;
      })() && (
        <section className="deals-section">
          <div className="container">
            <div className="deals-header">
              <div className="deals-heading">
                <h2 className="deals-title">
                  <i className="fas fa-gift deals-icon"></i>
                  Deals for you
                </h2>
                <p className="deals-subtitle">Exclusive offers just for you</p>
              </div>
              <div className="deals-actions">
                <button type="button" className="deal-arrow prev" onClick={() => dealsSliderRef.current?.prev()}>
                  <span className="fas fa-arrow-left" aria-hidden="true"></span>
                </button>
                <button type="button" className="deal-arrow next" onClick={() => dealsSliderRef.current?.next()}>
                  <span className="fas fa-arrow-right" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            <div className="deals-container">
              <CuponSlide ref={dealsSliderRef} restOffer={restaurantInformation.offer} />
            </div>
          </div>
        </section>
      )}

      {/* Download App Banner – online ordering only on mobile app (Zomato-style) */}
      <section className="download-app-banner-section">
        <div className="container download-app-banner-sticky">
          <DownloadAppBanner />
        </div>
      </section>

      {/* Menu Section */}
      <section className="menu-section">
        <div className="container">
          <Recommended
            restaurantInformation={restaurantInformation}
            restaurantMenu={getMenuData()}
            filter={filter}
            setFilter={setFilter}
            fatchapi={fetchRestauentData}
            isLoading={isLoading || petpoojaLoading}
            isPetpoojaIntegrated={isPetpoojaIntegrated}
            petpoojaID={petpoojaID}
          />
        </div>
      </section>

      {/* Tracking Bar */}
      <TrackingBar />

      {/* Mobile Bottom Bar */}
      <MobileBottomBar />
    </>
  );
}

export default RestaurentList;
